import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import differenceInCalendarMonths from 'date-fns/difference_in_calendar_months';

import {
  DataBlocks,
  Table,
  I18n,
  Link,
  TabsList,
  Tab,
} from 'components';

import {
  formatDateString,
  formatUnitString,
} from 'utils/formatString';

import {
  API_DATE_FORMAT,
  UI_DATE_FORMAT,
  STATUSES,
  TABS,
} from '../../constants';

import Status from '../Status';

import styles from './styles.pcss';

import {
  getPercentage,
  getMaxInvoiceSum,
  getAmountPayment,
  isAllPaid,
} from './utils';

const cx = classNames.bind(styles);

class PostpaidInvoices extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      activeTab: TABS.ALL,
    };
  }

  applyFilter() {
    const { invoices } = this.props;

    const { activeTab } = this.state;

    if (activeTab === TABS.UNPAID) {
      return invoices.filter(({ status }) => status === STATUSES.UNPAID);
    }

    return invoices;
  }

  renderTotalSection() {
    const { invoices, currency } = this.props;

    if (isAllPaid(invoices)) return <I18n path="detalization.postpaidInvoices.allPaid" />;

    return (
      <>
        <I18n path="detalization.postpaidInvoices.payment" />
        {`: ${formatUnitString(getAmountPayment(invoices), currency)}`}
      </>
    );
  }

  render() {
    const {
      invoices,
      currency,
      onPeriodClick,
      onInvoiceNumberClick,
      paymentUrl,
      startDateOfCurrentPeriod,
    } = this.props;

    const { activeTab } = this.state;

    if (!invoices || invoices.length === 0) return null;

    const maxInvoiceSum = getMaxInvoiceSum(invoices);

    return (
      <DataBlocks.ContentGroup
        title={<I18n path="detalization.postpaidInvoices.invoicePeriodsTitle" />}
      >
        <TabsList
          className={cx('tabs')}
          type="segmented"
          activeTab={activeTab}
          handleTabClick={(value) => this.setState({ activeTab: value })}
        >
          <Tab id={TABS.ALL}>
            <I18n path="detalization.postpaidInvoices.allInvoices" />
          </Tab>
          <Tab id={TABS.UNPAID}>
            <I18n path="detalization.postpaidInvoices.notPaidInvoices" />
          </Tab>
        </TabsList>
        {(this.applyFilter().length !== 0)
        && (
        <Table
          data={this.applyFilter()}
          config={{
            columns: [
              {
                name: 'detalization.postpaidInvoices.period',
                dataField: 'startDate',
                transformer: ({ startDate, endDate, invoiceNumber }) => (
                  differenceInCalendarMonths(new Date(startDateOfCurrentPeriod), new Date(startDate)) < 6 ? (
                    <Link
                      onClick={() => onPeriodClick({
                        invoiceNumber,
                        startDate: formatDateString(startDate, API_DATE_FORMAT),
                        endDate: formatDateString(endDate, API_DATE_FORMAT),
                      })}
                    >
                      {formatDateString(startDate, UI_DATE_FORMAT)}
                      {' — '}
                      {formatDateString(endDate, UI_DATE_FORMAT)}
                    </Link>
                  ) : (
                    <>
                      {formatDateString(startDate, UI_DATE_FORMAT)}
                      {' — '}
                      {formatDateString(endDate, UI_DATE_FORMAT)}
                    </>
                  )
                ),
                hideOnMobile: true,
              },
              {
                name: 'detalization.postpaidInvoices.group',
                dataField: 'benNumber',
                hideOnMobile: true,
              },
              {
                name: 'detalization.postpaidInvoices.mail',
                dataField: 'invoiceNumber',
                transformer: (invoice) => (
                  <Link
                    onClick={() => onInvoiceNumberClick(invoice)}
                  >
                    {invoice.invoiceNumber}
                  </Link>
                ),
              },
              {
                name: 'detalization.postpaidInvoices.sum',
                dataField: 'amount',
                width: '180px',
                transformer: ({ amount }) => (
                  <div className={cx('sumWrapper')}>
                    {formatUnitString(amount, currency)}
                    <div className={cx('sumPercentageWrapper')}>
                      <div
                        className={cx('sumPercentage')}
                        style={{ width: `${getPercentage(amount, maxInvoiceSum)}%` }}
                      />
                    </div>
                  </div>
                ),
                hideOnMobile: true,
              },
              {
                name: 'detalization.postpaidInvoices.status',
                dataField: 'status',
                width: '200px',
                transformer: (data) => (
                  <Status
                    {...data}
                    currency={currency}
                    paymentUrl={paymentUrl}
                  />
                ),
              },
            ],
            uniqueIdField: 'invoiceNumber',
          }}
        />
        )}
        <div className={cx('total')}>
          {this.renderTotalSection()}
        </div>
      </DataBlocks.ContentGroup>
    );
  }
}

PostpaidInvoices.defaultProps = {
  startDateOfCurrentPeriod: Date.now(),
};

PostpaidInvoices.propTypes = {
  invoices: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number,
      benNumber: PropTypes.number,
      dueDate: PropTypes.string,
      endDate: PropTypes.string,
      invoiceNumber: PropTypes.string,
      startDate: PropTypes.string,
      status: PropTypes.string,
      unpaidAmount: PropTypes.number,
    }),
  ),
  startDateOfCurrentPeriod: PropTypes.any,
  currency: PropTypes.string,
  paymentUrl: PropTypes.string,
  onPeriodClick: PropTypes.func,
  onInvoiceNumberClick: PropTypes.func,
};

export default PostpaidInvoices;
